import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  activateMiniGamePlayer,
  finishGameSession,
  GAME_LAUNCH_EVENT,
  GAME_PLAYER_ACTIVITY_EVENT,
  isGameLaunchLockedByAnotherWindow,
  readStoredGameLaunch,
  GAME_PLAYER_FRAME_LOADED_EVENT,
} from "../../../lib/gameLaunch";
import { GamePlayerGlobalStyle, StyledGamePlayerPage } from "./StyledGamePlayerPage";
import { isGameFrameLoaded } from '../../../features/gamePlayer/state/frameLoad.state';
import { isGamePopupActive, returnGameInline, requestGamePopupManualClose, reconcileGamePopup } from '../../../features/gamePlayer/lifecycle/mobilePopout.lifecycle';
import { isSameGameLaunchSnapshot } from '../../../features/gamePlayer/policy/launchUrl.policy';

const getPlayerKindLabel = (kind) => {
  if (kind === "provider") return "벤더 로비";
  if (kind === "game") return "게임 실행";
  return "게임 실행";
};

const GamePlayerPage = () => {
  const navigate = useNavigate();
  const [isFrameLoading, setIsFrameLoading] = useState(() => !isGameFrameLoaded(readStoredGameLaunch()));
  const [playerMode, setPlayerMode] = useState("default");
  const [desktopViewport, setDesktopViewport] = useState(() => window.innerWidth >= 1024);
  const [externalPopup, setExternalPopup] = useState(isGamePopupActive);
  const [manualCloseNotice, setManualCloseNotice] = useState(null);
  const [launch, setLaunch] = useState(() => readStoredGameLaunch());
  const [lockConflict, setLockConflict] = useState(() =>
    launch?.playUrl ? isGameLaunchLockedByAnotherWindow() : false
  );
  const manualCloseIntent = isGamePopupActive() && isSameGameLaunchSnapshot(manualCloseNotice?.launch, launch)
    ? manualCloseNotice.intent : null;

  useEffect(() => { setManualCloseNotice(null); }, [desktopViewport]);

  useEffect(() => {
    const handleLoaded = () => setIsFrameLoading(false);
    window.addEventListener(GAME_PLAYER_FRAME_LOADED_EVENT, handleLoaded);
    return () => window.removeEventListener(GAME_PLAYER_FRAME_LOADED_EVENT, handleLoaded);
  }, []);

  useEffect(() => {
    const handleGameLaunch = (event) => {
      const nextLaunch = event?.detail?.playUrl ? event.detail : readStoredGameLaunch();
      setIsFrameLoading(!isGameFrameLoaded(nextLaunch));
      setLockConflict(false);
      setLaunch(nextLaunch?.playUrl ? nextLaunch : null);
      setDesktopViewport(window.innerWidth >= 1024);
      setExternalPopup(isGamePopupActive());
      setManualCloseNotice(current => event?.type !== GAME_LAUNCH_EVENT && isGamePopupActive() &&
        isSameGameLaunchSnapshot(current?.launch, nextLaunch) ? current : null);
    };

    window.addEventListener(GAME_LAUNCH_EVENT, handleGameLaunch);
    window.addEventListener(GAME_PLAYER_ACTIVITY_EVENT, handleGameLaunch);
    window.addEventListener('resize', handleGameLaunch);
    return () => {
      window.removeEventListener(GAME_LAUNCH_EVENT, handleGameLaunch);
      window.removeEventListener(GAME_PLAYER_ACTIVITY_EVENT, handleGameLaunch);
      window.removeEventListener('resize', handleGameLaunch);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.documentElement.classList.add("crypto-game-player-route");
    document.body.classList.add("crypto-game-player-route");

    return () => {
      document.documentElement.classList.remove("crypto-game-player-route");
      document.body.classList.remove("crypto-game-player-route");
      document.documentElement.classList.remove("crypto-game-player-theater");
      document.body.classList.remove("crypto-game-player-theater");
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const shouldUseTheater = playerMode === "theater";
    document.documentElement.classList.toggle("crypto-game-player-theater", shouldUseTheater);
    document.body.classList.toggle("crypto-game-player-theater", shouldUseTheater);

    return () => {
      document.documentElement.classList.remove("crypto-game-player-theater");
      document.body.classList.remove("crypto-game-player-theater");
    };
  }, [playerMode]);

  const handleExit = () => {
    if (externalPopup || isGamePopupActive()) {
      if (!isGamePopupActive()) reconcileGamePopup();
      else if (requestGamePopupManualClose({ restoreInline: false })) setManualCloseNotice({ launch, intent: 'exit' });
      return;
    }
    const sourcePath = launch?.sourcePath && launch.sourcePath !== "/play" ? launch.sourcePath : "/";
    finishGameSession("game-player-exit");
    navigate(sourcePath, { replace: true });
  };

  const handleChangeMode = (nextMode) => {
    if (requestPopupReturn()) return;
    if (nextMode === "mini") {
      activateMiniGamePlayer();
      const sourcePath =
        launch?.sourcePath && launch.sourcePath !== "/play" ? launch.sourcePath : "/";
      navigate(sourcePath, { replace: true });
      return;
    }

    setPlayerMode((currentMode) => (currentMode === nextMode ? "default" : nextMode));
  };

  const requestPopupReturn = () => {
    if (!externalPopup && !isGamePopupActive()) return false;
    if (!isGamePopupActive()) reconcileGamePopup();
    else if (returnGameInline()) setManualCloseNotice({ launch, intent: 'return' });
    return true;
  };

  if (!launch?.playUrl || lockConflict) {
    return (
      <StyledGamePlayerPage>
        <GamePlayerGlobalStyle />
        <div className="game-player-empty">
          <div>
            <h1>{lockConflict ? "이미 실행 중인 게임이 있습니다." : "실행할 게임 정보가 없습니다."}</h1>
            <p>
              {lockConflict
                ? "다른 창의 게임을 종료한 뒤 다시 실행해주세요."
                : "게임 목록에서 다시 실행해주세요."}
            </p>
            <button className="game-player-action primary" type="button" onClick={handleExit}>
              홈으로 이동
            </button>
          </div>
        </div>
      </StyledGamePlayerPage>
    );
  }

  const title = launch.label || "게임";
  const vendorText = launch.vendorName || getPlayerKindLabel(launch.kind);

  return (
    <StyledGamePlayerPage>
      <GamePlayerGlobalStyle />
      <div className={`game-player-shell is-${playerMode}`}>
        <section className="game-player-stage" aria-label={`${title} 실행 화면`}>
          <div data-game-frame-slot="full" className="game-player-frame" />
          {externalPopup && desktopViewport ? <div className="game-player-loading" role="status" aria-live="polite" style={{ pointerEvents: 'auto' }}>
            <div className="game-player-loading-card">
              <strong>게임이 새 창에서 실행 중입니다.</strong>
              <span>{manualCloseIntent === 'return'
                ? '게임 새창을 직접 닫아주세요. 닫힘 확인 후 내부 화면으로 돌아옵니다.'
                : manualCloseIntent === 'exit'
                  ? '게임 새창을 직접 닫아주세요. 닫힘 확인 후 게임이 종료됩니다.'
                  : '복귀·종료 선택 후 게임 새창을 직접 닫아주세요.'}</span>
              <button className="game-player-action primary" type="button" onClick={requestPopupReturn}>새창 닫고 내부로 복귀</button>
              <button className="game-player-action primary" type="button" onClick={handleExit}>새창 닫고 게임 종료</button>
            </div>
          </div> : <div
            className={`game-player-loading ${isFrameLoading && !externalPopup ? "" : "is-hidden"}`}
            aria-live="polite"
            role="status"
          >
            <div className="game-player-loading-card">
              <span className="game-player-loading-mark" aria-hidden="true" />
              <span>게임 화면을 불러오는 중입니다.</span>
            </div>
          </div>}
        </section>

        <section className="game-player-controlbar" aria-label="게임 실행 도구">
          <div className="game-player-title">
            <span>{vendorText}</span>
            <h1>{title}</h1>
          </div>

          <div className="game-player-actions">
            <button
              className={`game-player-action mode ${playerMode === "mini" ? "is-active" : ""}`}
              type="button"
              onClick={() => handleChangeMode("mini")}
            >
              미니플레이어
            </button>
            <button
              className={`game-player-action mode ${playerMode === "theater" ? "is-active" : ""}`}
              type="button"
              onClick={() => handleChangeMode("theater")}
            >
              {playerMode === "theater" ? "기본모드" : "영화관모드"}
            </button>
          </div>
        </section>
      </div>
      <button
        aria-label="모바일 게임 종료"
        className="game-player-mobile-exit"
        type="button"
        onClick={handleExit}
      >
        게임 종료
      </button>
    </StyledGamePlayerPage>
  );
};

export default GamePlayerPage;
